/**
 * Inline Annotations Types
 *
 * Type definitions for the inline text annotation system.
 * Combines API response types into UI-friendly structures
 * for the inline highlight experience.
 */

import type {
  IssueSeverity,
  IssueType,
} from '@dramadetector/shared';

/**
 * Issue status in the UI workflow
 */
export type IssueStatus = 'pending' | 'accepted' | 'ignored';

/**
 * View mode for the segmented control
 */
export type ViewMode = 'original' | 'revised';

/**
 * Unified issue structure for the inline annotations UI
 * Merges DramaIssue with its corresponding SuggestedRewrite
 */
export interface InlineIssue {
  /** Unique identifier (based on index) */
  id: number;
  /** Issue type from API */
  type: IssueType;
  /** Severity level */
  severity: IssueSeverity;
  /** Human-readable title (derived from type) */
  title: string;
  /** Problematic text snippet */
  snippet: string;
  /** Full original text (for replacement context) */
  original: string;
  /** Start position in message text */
  startIndex: number;
  /** End position in message text */
  endIndex: number;
  /** Description of why this is problematic */
  description: string;
  /** Suggested replacement text */
  suggestion: string;
  /** Clean replacement text (without quotes) */
  replacement: string;
  /** Current status in accept/ignore workflow */
  status: IssueStatus;
  /** Drama reduction percentage (0-100) */
  dramaReduction: number;
  /** Score contribution weight from backend (0-1), used for weighted revised score */
  scoreWeight?: number;
  /** Explanation bullet points */
  explanation: string[];
}

/**
 * Text segment for rendering highlighted message
 */
export interface TextSegment {
  /** Text content */
  text: string;
  /** Whether this is an issue highlight */
  isIssue: boolean;
  /** Reference to issue if isIssue is true */
  issue?: InlineIssue;
  /** Display index (1-based) for footnote superscript */
  issueNumber?: number;
}

/**
 * Segment for revised view with replacement highlighting
 */
export interface RevisedSegment {
  /** Text content */
  text: string;
  /** Whether this segment is a replacement from an accepted issue */
  isReplacement: boolean;
  /** Original text that was replaced (for tooltip/accessibility) */
  originalText?: string;
  /** Reference to source issue ID */
  issueId?: number;
}

/**
 * REMOVED: Duplicate InlineMessageDisplayProps interface.
 *
 * This interface was superseded by the canonical definition in InlineMessageDisplay.tsx.
 * The InlineMessageDisplay.tsx version includes tier-gating (tier, screen, onUpgrade)
 * and revision tracking (tracking prop) that this version lacked.
 *
 * Canonical source: InlineMessageDisplay.tsx line 71
 * Removed: 2026-02-12
 */

/**
 * Anchor position for popover/bottom sheet positioning
 */
export interface AnchorPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * Props for IssueHighlight component
 */
export interface IssueHighlightProps {
  /** The issue being highlighted */
  issue: InlineIssue;
  /** Display index for footnote superscript */
  issueNumber: number;
  /** Whether this highlight is currently focused/selected */
  isActive: boolean;
  /** Callback when mouse hovers (web) - triggers delayed popover */
  onHover?: (issueId: number, position: AnchorPosition) => void;
  /** Callback when mouse leaves (web) - clears hover timer */
  onHoverEnd?: () => void;
  /** Callback when pressed/clicked - triggers immediate toggle */
  onPress?: (issueId: number, position: AnchorPosition) => void;
  /** Optional test ID */
  testID?: string;
}

/**
 * Props for IssuePopover (web) component
 */
export interface IssuePopoverProps {
  /** The issue to display */
  issue: InlineIssue;
  /** Whether the popover is visible */
  isVisible: boolean;
  /** Anchor position for positioning */
  anchorPosition: { x: number; y: number; width: number; height: number };
  /** Callback when accept button pressed */
  onAccept: () => void;
  /** Callback when ignore button pressed */
  onIgnore: () => void;
  /** Callback to close popover */
  onClose: () => void;
  /** Callback when mouse enters popover (prevents close) */
  onMouseEnter?: () => void;
  /** Callback when mouse leaves popover */
  onMouseLeave?: () => void;
  /** Optional test ID */
  testID?: string;
  // Navigation props (for parity with native bottom sheet)
  /** All issues for navigation */
  issues?: InlineIssue[];
  /** Currently selected issue index */
  currentIndex?: number;
  /** Callback when navigating to previous issue */
  onPrevious?: () => void;
  /** Callback when navigating to next issue */
  onNext?: () => void;
}

/**
 * Props for IssueBottomSheet (native) component
 */
export interface IssueBottomSheetProps {
  /** All issues for navigation */
  issues: InlineIssue[];
  /** Currently selected issue index */
  currentIndex: number;
  /** Whether the bottom sheet is visible */
  isVisible: boolean;
  /** Callback when navigating to previous issue */
  onPrevious: () => void;
  /** Callback when navigating to next issue */
  onNext: () => void;
  /** Callback when accept button pressed */
  onAccept: () => void;
  /** Callback when ignore button pressed */
  onIgnore: () => void;
  /** Callback to close bottom sheet */
  onClose: () => void;
  /** Optional test ID */
  testID?: string;
}

/**
 * Props for SegmentedControl component
 */
export interface SegmentedControlProps {
  /** Current view mode */
  value: ViewMode;
  /** Callback when value changes */
  onChange: (mode: ViewMode) => void;
  /** Whether the revised view has changes */
  hasChanges: boolean;
  /** Optional test ID */
  testID?: string;
}

/**
 * Props for CopyRevisedBar component
 */
export interface CopyRevisedBarProps {
  /** The revised text to copy */
  revisedText: string;
  /** Number of accepted suggestions */
  acceptedCount: number;
  /** Total number of issues */
  totalCount: number;
  /** Whether there are changes to copy */
  hasChanges: boolean;
  /** Optional test ID */
  testID?: string;
}

/**
 * Props for ProgressIndicator component
 */
export interface ProgressIndicatorProps {
  /** Number of issues addressed (accepted + ignored) */
  addressedCount: number;
  /** Total number of issues */
  totalCount: number;
  /** Optional test ID */
  testID?: string;
}

/**
 * Props for ScoreDeltaIndicator component (Phase 2: Gamification)
 *
 * Shows real-time score improvement as "+X points" badge to drive
 * engagement with RLHF revision tracking.
 *
 * @see REVISION_TRACKING_DATA_MODEL.md for architecture
 */
export interface ScoreDeltaIndicatorProps {
  /** Score improvement (positive = improvement, 0 = no change) */
  scoreDelta: number;
  /** Whether to show the indicator (only when delta > 0) */
  isVisible?: boolean;
  /** Animation delay in ms (for staggering multiple indicators) */
  animationDelay?: number;
  /** Callback when animation completes */
  onAnimationComplete?: () => void;
  /** Optional test ID */
  testID?: string;
}

/**
 * Revision tracking configuration (optional)
 * When provided, enables RLHF data collection for rewrite suggestions
 */
export interface RevisionTrackingConfig {
  /** Analysis ID from database (required) */
  analysisId: string;
  /** Original drama score (0-10) */
  originalScore: number;
  /** Original summary text */
  originalSummary?: string;
  /** Agent execution ID for attribution */
  agentExecutionId?: string;
  /** Model ID for filtering */
  modelId?: string;
  /** Whether tracking is enabled (defaults to true when config provided) */
  enabled?: boolean;
  /** Callback when tracking error occurs (for Sentry) */
  onTrackingError?: (error: Error, context: string) => void;
}

/**
 * Severity color configuration
 */
export const SEVERITY_COLORS = {
  high: {
    bg: 'rgba(220, 38, 38, 0.20)',
    text: '#7F1D1D',
    border: '#DC2626',
  },
  medium: {
    bg: 'rgba(217, 119, 6, 0.20)',
    text: '#78350F',
    border: '#D97706',
  },
  low: {
    bg: 'rgba(22, 163, 74, 0.20)',
    text: '#14532D',
    border: '#16A34A',
  },
  critical: {
    bg: 'rgba(124, 58, 237, 0.20)',
    text: '#3730A3',
    border: '#7C3AED',
  },
} as const;

/**
 * Issue type to title mapping
 */
export const ISSUE_TYPE_TITLES: Record<IssueType, string> = {
  'passive-aggressive': 'Passive-Aggressive',
  'blame-shifting': 'Blame-Shifting',
  'emotional-manipulation': 'Emotional Manipulation',
  absolutes: 'Absolute Statement',
  dismissive: 'Dismissive Language',
  'aggressive-tone': 'Aggressive Tone',
  defensiveness: 'Defensiveness',
  contempt: 'Contempt',
};
